import { usePubNub } from 'pubnub-react';
import { useNavigate } from 'react-router-dom';
import {
  batchAttendUnattendSession,
  getSession,
  privateCallActions,
  useCreatePrivateCall,
} from '@juno/client-api';
import { CommunityUser, PrivateCallStatusEnum, Session } from '@juno/client-api/model';
import { MutationAction, onMutation } from '../FormUtils';
import { navigateToSession, openSessionInNewTab } from '../NavigationUtils';
import { CallUnansweredStatus, useSessionContext } from '../contexts/SessionContext';
import { useSettings } from '../routing/RoutingMediator';

export enum PrivateCallMessageType {
  initiated = 'private_call:initiated',
  answered = 'private_call:answered',
  declined = 'private_call:declined',
  timedOut = 'private_call:timed_out',
  cancelled = 'private_call:cancelled',
}

export const usePrivateCall = () => {
  const createPrivateCall = useCreatePrivateCall(onMutation(MutationAction.CREATE, '', () => {}));

  const { user, site } = useSettings();
  const { callState, setCallState } = useSessionContext();
  const navigate = useNavigate();

  const handleAccept = async (withVideo: boolean) => {
    if (callState?.otherUser?.id && callState.sessionId) {
      try {
        const session = await getSession(site?.id || '', callState.sessionId);

        // Add both users if not already attendees
        await batchAttendUnattendSession(site?.id || '', callState.sessionId, [
          { user_id: callState.otherUser.id, is_add: true },
          { user_id: user?.id || '', is_add: true },
        ]);
      } catch (error) {
        console.error('Error adding users to session attendees:', error);
      }

      const m = {
        type: PrivateCallMessageType.answered,
      };

      await privateCallActions(site?.id || '', {
        recipient_user_id: callState.otherUser.id,
        message: m,
      });

      navigateToSession(navigate, site?.slug || '', callState.sessionId || '', true, withVideo);

      setCallState({});
    }
  };

  const handleJoin = (withVideo: boolean) => {
    navigateToSession(navigate, site?.slug || '', callState.sessionId || '', true, withVideo);

    if (!callState.isCaller) {
      setCallState({});
    } else {
      setCallState((old) => ({ ...old, hasJoined: true }));
    }
  };

  const handleJoinInNewTab = (withVideo: boolean) => {
    openSessionInNewTab(site?.slug || '', callState.sessionId || '', true, withVideo);

    if (!callState.isCaller) {
      setCallState({});
    } else {
      setCallState((old) => ({ ...old, hasJoined: true }));
    }
  };

  const handleDecline = async () => {
    if (callState?.otherUser?.id) {
      const m = {
        type: 'private_call:declined',
        name: `${user?.first_name} ${user?.last_name}`,
        id: user?.id,
      };

      await privateCallActions(site?.id || '', {
        recipient_user_id: callState.otherUser.id,
        message: m,
      });

      setCallState({
        ...callState,
        status: PrivateCallStatusEnum.declined,
        dialogOpen: true,
        dialogStatus: CallUnansweredStatus.DECLINED,
      });
    }
  };

  const handleTimeout = async () => {
    if (callState?.otherUser?.id) {
      const m = {
        type: PrivateCallMessageType.timedOut,
      };

      await privateCallActions(site?.id || '', {
        recipient_user_id: callState.otherUser.id,
        message: m,
      });

      setCallState({});
    }
  };

  const handleCancel = async () => {
    const m = {
      type: PrivateCallMessageType.cancelled,
    };

    await privateCallActions(site?.id || '', {
      recipient_user_id: callState.otherUser?.id || '',
      message: m,
    });
  };

  const startPrivateCall = async (otherUser: CommunityUser) => {
    try {
      const privateCall = await createPrivateCall.mutateAsync({
        siteId: site?.id || '',
        data: {
          caller: user?.id || '',
          callee: otherUser.id,
          site: site?.id || '',
          session: {} as Session,
          status: PrivateCallStatusEnum.pending,
        },
      });

      // Add both users to scheduled attendees if session was created
      // ensure the caller sees attendees immediately after the call is created
      if (privateCall.session?.id) {
        try {
          await batchAttendUnattendSession(site?.id || '', privateCall.session.id, [
            { user_id: user?.id || '', is_add: true },
            { user_id: otherUser.id, is_add: true },
          ]);
        } catch (error) {
          console.error('Error adding users to session attendees:', error);
        }
      }

      const m = {
        type: PrivateCallMessageType.initiated,
        caller: {
          id: user?.id,
          first_name: user?.first_name,
          last_name: user?.last_name,
          avatar: user?.avatar,
        },
        sessionId: privateCall.session?.id,
        privateCallId: privateCall.id,
      };

      await privateCallActions(site?.id || '', { recipient_user_id: otherUser.id, message: m });

      setCallState({
        status: PrivateCallStatusEnum.pending,
        otherUser: {
          id: otherUser.id,
          first_name: otherUser.first_name,
          last_name: otherUser.last_name,
          avatar: otherUser.avatar || '',
        },
        sessionId: privateCall.session?.id,
        privateCallId: privateCall.id,
        isCaller: true,
      });
    } catch (e) {
      console.error('Error creating private call', e);
    }
  };

  return {
    handleAccept,
    handleDecline,
    handleTimeout,
    startPrivateCall,
    handleCancel,
    handleJoin,
    handleJoinInNewTab,
  };
};
